import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { httpBatchLink } from '@trpc/client';
import { getQueryKey } from '@trpc/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '#web/components/theme-provider';
import { Toaster } from '#web/components/ui/sonner';
import { api, apiErrorCode } from './api';
import { installGlobalErrorReporting, reportBrowserError, reportReactError } from './error-reporting';
import { routeTree } from './routeTree.gen';
import { createTrpcFetch } from './trpc-fetch';
import './index.css';

/** Failures a retry can't fix. */
const FINAL_ERROR_CODES = new Set(['BAD_REQUEST', 'FORBIDDEN', 'NOT_FOUND', 'UNAUTHORIZED']);

function handleApiError(context: string, error: unknown) {
  reportBrowserError(context, error);
  // The server has already dropped a dead session; re-check it so the layout sends the user to /login.
  if (apiErrorCode(error) === 'UNAUTHORIZED') {
    void queryClient.invalidateQueries({ queryKey: getQueryKey(api.auth.me) });
  }
}

const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => failureCount < 2 && !FINAL_ERROR_CODES.has(apiErrorCode(error) ?? ''),
    },
  },
  queryCache: new QueryCache({ onError: (error) => handleApiError('query.failed', error) }),
  mutationCache: new MutationCache({ onError: (error) => handleApiError('mutation.failed', error) }),
});

const trpcClient = api.createClient({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      fetch: createTrpcFetch({ request: (input, init) => globalThis.fetch(input, init) }),
    }),
  ],
});

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

installGlobalErrorReporting();

// biome-ignore lint/style/noNonNullAssertion: root element always exists in index.html
const root = ReactDOM.createRoot(document.getElementById('root')!, {
  onCaughtError: (error, errorInfo) => reportReactError('react.caught', error, errorInfo),
  onRecoverableError: (error, errorInfo) => reportReactError('react.recoverable', error, errorInfo),
  onUncaughtError: (error, errorInfo) => reportReactError('react.uncaught', error, errorInfo),
});

root.render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="system">
      <api.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </api.Provider>
      <Toaster />
    </ThemeProvider>
  </React.StrictMode>,
);
